import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { api, apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { useAppData } from "../context/AppDataContext";

export default function AdultGateDialog() {
  const { adultGate, setAdultGate } = useAppData();
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [birth, setBirth] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!adultGate) return;
    setBirth(user?.birth_date || "");
    setConfirm(false);
    setError("");
    setBlocked(false);
  }, [adultGate]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => setAdultGate(null);
  const minor = blocked || (user && user.birth_date && !user.is_adult);

  const enter = async () => {
    setError("");
    if (!birth) return setError(t("err.required"));
    if (!confirm) return setError(t("gate.needConfirm"));
    setBusy(true);
    try {
      const { data } = await api.post("/users/me/adult-confirm", { birth_date: user.birth_date ? null : birth, confirm: true });
      setUser(data);
      const pass = adultGate.onPass;
      close();
      pass();
    } catch (e) {
      if (e.response?.status === 403) {
        setBlocked(true);
        api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
      } else setError(apiError(e, t));
    }
    setBusy(false);
  };

  return (
    <Dialog open={!!adultGate} onOpenChange={(o) => !o && close()}>
      <DialogContent className="w-[calc(100%-2.5rem)] max-w-sm overflow-visible rounded-3xl border-0 px-6 pb-6 pt-14 [&>button]:hidden" data-testid="adult-gate-dialog">
        <div className="absolute -top-12 left-1/2 flex h-24 w-24 -translate-x-1/2 items-center justify-center rounded-full bg-alert text-4xl font-extrabold text-white shadow-[0_10px_30px_rgba(229,57,53,0.4)] ring-4 ring-white" data-testid="adult-gate-badge">
          +18
        </div>
        <DialogTitle className="text-center text-2xl font-extrabold leading-tight text-navy">
          {minor ? t("gate.minorTitle") : t("gate.title")}
        </DialogTitle>
        <DialogDescription className="text-center text-[15px] text-slate-500" data-testid="adult-gate-message">
          {!user ? t("gate.loginSub") : minor ? t("gate.minorSub") : t("gate.sub")}
        </DialogDescription>

        {user && !minor && (
          <div className="space-y-3">
            <label className="block rounded-2xl border border-slate-300 px-4 pb-2 pt-1.5 focus-within:border-brand">
              <span className="block text-xs text-slate-500">{t("auth.birthDate")}</span>
              <input type="date" value={birth} disabled={!!user.birth_date} max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setBirth(e.target.value)} className="w-full bg-transparent text-[16px] text-navy outline-none disabled:text-slate-500"
                data-testid="adult-gate-birthdate-input" />
            </label>
            <label className="flex cursor-pointer items-center gap-3 text-[15px] text-navy">
              <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} className="h-5 w-5 rounded-md border-slate-400 data-[state=checked]:border-brand data-[state=checked]:bg-brand" data-testid="adult-gate-confirm-checkbox" />
              {t("gate.confirm")}
            </label>
            {error && <p className="text-sm font-semibold text-alert" data-testid="adult-gate-error">{error}</p>}
          </div>
        )}

        <div className="mt-1 flex flex-col items-center gap-2">
          {!user && (
            <button className="btn-primary w-full" onClick={() => { close(); navigate("/login", { state: { from: window.location.pathname } }); }} data-testid="adult-gate-login-btn">
              {t("auth.login")}
            </button>
          )}
          {user && !minor && (
            <button className="btn-primary w-full" onClick={enter} disabled={busy} data-testid="adult-gate-enter-btn">{t("gate.enter")}</button>
          )}
          <button className="py-2 font-bold text-navy hover:underline" onClick={close} data-testid="adult-gate-back-btn">{t("gate.back")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
